/**
 * AusschreibungenListScreen - the FIRM-WIDE «Ausschreibungen» list (sidebar entry, route
 * /ausschreibungen). Every Placement of the tenant across all customers, where the per-customer
 * `AusschreibungenTab` shows one customer's.
 *
 * T1 List-View chrome (`ListView`): the open/closed status filter as the saved-view pills (with
 * counts), a search box (Nr. · Sparte · Kunde), a Liste|Pipeline SegmentedControl top-right, and
 * the KPI row + the list/pipeline underneath. The columns, the status filter and the pipeline are
 * the SAME building blocks as the Kunde tab (`placementListColumns`, `matchesPlacementFilter`,
 * `PlacementPipeline`) plus one «Kunde» column / card line linking to /kunden/$id.
 *
 * Data: GET /placements with NO subject (`usePlacementsForFirm`) - the server owns the rows + KPIs;
 * this screen only filters/searches client-side and renders. No «Neues Placement» here: a Placement
 * is opened from its customer (the tab), never without a subject.
 *
 * Gate: a caller without `placement.read` neither fetches nor sees the list (the api guard stays
 * the hard line); the sidebar entry itself is unconditional (nav.ts precedent: Administration).
 *
 * [Source: apps/web/src/features/placements/AusschreibungenTab.tsx] (building blocks)
 * [Source: apps/web/src/features/policies/PoliciesScreen.tsx] (T1 ListView idiom)
 */

import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { CheckCircle2, ClipboardList, FileCheck2, Send } from 'lucide-react';
import { ListView } from '../../components/data/ListView';
import { DataTable } from '../../components/data/DataTable';
import { SegmentedControl } from '../../components/ui/SegmentedControl';
import { Button } from '../../components/ui/Button';
import { EntityLink } from '../../components/ui/EntityLink';
import { KpiCard } from '../entity-detail/components/KpiCard';
import { useEffectivePermissions } from '../administration/useEffectivePermissions';
import { usePlacementsForFirm } from './usePlacements';
import { placementListColumns } from './AusschreibungenTab';
import { PLACEMENT_STATUS_FILTER_CHIPS } from '@brokly/shared';
import type { PlacementListView } from '@brokly/shared';
import { PlacementPipeline } from './PlacementPipeline';
import type { PlacementFirmListItem } from './types';

const SEARCH_DEBOUNCE_MS = 300;

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function subjectLabel(item: PlacementFirmListItem): string {
  return item.subject_name?.trim() || '-';
}

export function AusschreibungenListScreen() {
  const navigate = useNavigate();
  const { can, isLoading: permsLoading } = useEffectivePermissions();
  const canRead = can('placement.read');

  const [viewIndex, setViewIndex] = useState(0);
  const [search, setSearch] = useState('');
  const [view, setView] = useState<'liste' | 'pipeline'>('liste');

  // The chip + the search are the SERVER legs now (#14A): the chip is the `view` param, the search
  // the `q` param (debounced so a keystroke does not fire a request each). Changing either resets
  // the infinite query (its key carries view + q).
  const chipFilter: PlacementListView = PLACEMENT_STATUS_FILTER_CHIPS[viewIndex]?.value ?? 'alle';
  const q = useDebounced(search.trim(), SEARCH_DEBOUNCE_MS);
  const query = usePlacementsForFirm(canRead, { view: chipFilter, q });

  const openDetail = (id: string) => void navigate({ to: '/ausschreibungen/$id', params: { id } });
  const openKunde = (id: string) => void navigate({ to: '/kunden/$id', params: { id } });

  const items = query.data?.pages.flatMap((p) => p.items) ?? [];
  const first = query.data?.pages[0];
  const kpis = first?.kpis;
  const viewCounts = first?.view_counts;

  // perf-review MINOR: the server's view_counts (and KPIs) ignore `q` by design, so while a search is
  // active they overstate the result set. Reflect the FILTERED rows we actually have: the loaded items
  // for the active view. The other views' filtered totals are not known client-side, so their chip
  // counts are hidden while searching rather than shown as the misleading unfiltered total.
  const searching = q.length > 0;
  const total = searching ? items.length : (viewCounts?.[chipFilter] ?? 0);

  const views = PLACEMENT_STATUS_FILTER_CHIPS.map((c, i) => ({
    label: c.label,
    count: searching ? (i === viewIndex ? items.length : undefined) : (viewCounts?.[c.value] ?? 0),
  }));

  const columns = placementListColumns<PlacementFirmListItem>(openDetail, {
    kundeCell: (item) => (
      <EntityLink onActivate={() => openKunde(item.subject_id)} className="text-sm">
        {subjectLabel(item)}
      </EntityLink>
    ),
  });

  const loadMore = query.hasNextPage ? (
    <div className="mt-3 flex justify-center">
      <Button
        variant="outline"
        size="sm"
        data-testid="aul-load-more"
        disabled={query.isFetchingNextPage}
        onClick={() => void query.fetchNextPage()}
      >
        {query.isFetchingNextPage ? 'Lädt …' : 'Mehr laden'}
      </Button>
    </div>
  ) : null;

  let body: React.ReactNode;
  if (!permsLoading && !canRead) {
    body = (
      <p role="alert" className="text-sm text-muted-foreground" data-testid="aul-no-permission">
        Keine Berechtigung für Ausschreibungen.
      </p>
    );
  } else if (query.isLoading || (permsLoading && !query.data)) {
    body = <p className="text-sm text-muted-foreground">Lade Ausschreibungen…</p>;
  } else if (query.isError || !query.data) {
    body = (
      <p role="alert" className="text-sm text-status-danger" data-testid="aul-error">
        Die Ausschreibungen konnten nicht geladen werden.
      </p>
    );
  } else if (items.length === 0) {
    body = (
      <div className="rounded-lg border border-dashed bg-card p-8 text-center text-sm text-muted-foreground">
        {total === 0 ? 'Noch keine Ausschreibungen.' : 'Keine Treffer für den Filter.'}
      </div>
    );
  } else if (view === 'liste') {
    body = (
      <>
        <DataTable columns={columns} data={items} />
        {loadMore}
      </>
    );
  } else {
    body = (
      <>
        {query.hasNextPage && (
          <p className="mb-2 text-xs text-muted-foreground" data-testid="aul-pipeline-hint">
            Pipeline zeigt die ersten {items.length} von {total}
          </p>
        )}
        <PlacementPipeline items={items} onOpen={openDetail} cardMeta={subjectLabel} />
        {loadMore}
      </>
    );
  }

  return (
    <div data-testid="ausschreibungen-list-screen">
      <ListView
        title="Ausschreibungen"
        views={views}
        activeView={viewIndex}
        onViewChange={setViewIndex}
        count={`${items.length} von ${total}`}
        searchPlaceholder="Nr., Sparte oder Kunde suchen"
        searchValue={search}
        onSearchChange={setSearch}
        rightAction={
          <SegmentedControl
            ariaLabel="Ansicht"
            value={view}
            onValueChange={(v) => setView(v as 'liste' | 'pipeline')}
            options={[
              { value: 'liste', label: 'Liste' },
              { value: 'pipeline', label: 'Pipeline' },
            ]}
          />
        }
      >
        <div className="flex flex-col gap-5">
          {kpis && canRead && (
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <KpiCard icon={Send} label="Offen" value={String(kpis.offen)} />
              <KpiCard icon={ClipboardList} label="In Vorbereitung" value={String(kpis.in_vorbereitung)} />
              <KpiCard icon={FileCheck2} label="Beim Kunden" value={String(kpis.beim_kunden)} />
              <KpiCard icon={CheckCircle2} label="Erledigt" value={String(kpis.erledigt)} />
            </div>
          )}
          {body}
        </div>
      </ListView>
    </div>
  );
}
